import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { syncPrivacyCache } from '@/utils/privacyCacheSync';
import { Toaster } from 'react-hot-toast';
import { Login } from '@/pages/Login';
import { Signup } from '@/pages/Signup';
import { AdminRoute, InstitutionRoute } from '@/components/layout/RoleRoute';
import { BootScreen } from '@/components/common/ShellBoot';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

/**
 * 관리자 셸과 기관 포털은 **서로 다른 번들**이다.
 *
 * 같은 컴포넌트를 role 로 조건부 렌더링하면, 캐시에 남은 관리자 화면 조각이
 * 기관 사용자에게 잠깐이라도 보일 여지가 생긴다. 라우트 가드가 먼저 되돌려
 * 보내면 React.lazy 가 청크를 요청하지도 않으므로, 기관 계정은 관리자 코드를
 * 내려받는 일 자체가 없다(계약 §0-4, §6).
 */
const AdminApp = lazy(() => import('@/admin/AdminApp'));
const PortalApp = lazy(() => import('@/portal/PortalApp'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// 개인정보 가림을 풀거나 다시 걸 때 해당 캐시를 새로 받는다 (서버가 가림 여부를 정한다)
syncPrivacyCache(queryClient);

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            {/* 공개 화면 */}
            <Route path='/login' element={<Login />} />
            <Route path='/signup' element={<Signup />} />

            {/* 기관 담당자 포털 */}
            <Route
              path='/portal/*'
              element={
                <InstitutionRoute>
                  <Suspense fallback={<BootScreen message='포털을 불러오는 중...' />}>
                    <PortalApp />
                  </Suspense>
                </InstitutionRoute>
              }
            />

            {/* 관리자·뷰어 셸. 나머지 경로를 전부 받는다 */}
            <Route
              path='/*'
              element={
                <AdminRoute>
                  <Suspense fallback={<BootScreen message='관리 화면을 불러오는 중...' />}>
                    <AdminApp />
                  </Suspense>
                </AdminRoute>
              }
            />
          </Routes>
        </BrowserRouter>

        <Toaster
          position='bottom-right'
          toastOptions={{
            duration: 3000,
            style: {
              background: '#fff',
              color: '#363636',
            },
          }}
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
